import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Mail, MapPin, Phone } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const renderEmail = (email) => {
    const [localPart, domainPart] = email.split('@');

    if (!localPart || !domainPart) {
      return email;
    }

    return (
      <>
        {localPart}@<wbr />{domainPart}
      </>
    );
  };

  const offices = [
    {
      title: 'Qatar Office',
      address: 'PO Box 200091, #104, Block 11 Sayer-II, Barwa Commercial Avenue, Doha, Qatar.',
      email: 'contactqatar@hillpointglobal.com',
      phone: '+974-4032-8777',
      fax: '+974-4032-9091',
    },
    {
      title: 'UAE Office',
      address: 'PO Box 120077, AL Manara Tower, Office No.1211, Business Bay, Sheik Zayed Road, Dubai, UAE.',
      email: 'contactdubai@hillpointglobal.com',
      phone: '+971-04-3214942',
      fax: '+971-04-3214943',
    },
    {
      title: 'India Corporate Office',
      address: 'No 3rd, 2nd Floor, Dasarahalli Main Road, Opposite Karagadamba Temple, Dasarahalli, Hebbal post, Bangalore-560024, Karnataka, India.',
      email: 'contactindia@hillpointglobal.com',
      phones: ['+91-9790967946', '+91-9148068844'],
    },
    {
      title: 'Saudi Office',
      address: 'Jeddah, Building 1366, Arafat AL, Hamra district, Saudi Arabia.',
      email: 'contactsaudi@hillpointglobal.com',
    },
    {
      title: 'Manufacturing Unit',
      address: 'Coimbatore, Tamil Nadu, India.',
      email: 'contactindia@hillpointglobal.com',
    },
  ];

  return (
    <footer className="bg-hillpoint text-white pt-16 pb-8 border-t-4 border-gold">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        
        {/* Main Heading */}
        <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4 tracking-tight">Global Presence</h2>
            <div className="w-24 h-1 bg-gold mx-auto mb-6"></div>
            <p className="text-lg text-stone-300 max-w-2xl mx-auto font-light">Serving clients across multiple continents with acoustic excellence</p>
        </div>

        {/* Offices Grid - Masonry-ish Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-16">
        {offices.map((office, index) => (
            <div key={index} className="group relative min-w-0 p-5 border border-white/10 hover:border-gold/50 transition-all duration-500 hover:bg-white/5 rounded-sm">
                <h3 className="text-lg font-serif text-gold mb-4">{office.title}</h3>
                {/* Links carry py-3.5 so email/phone tap targets reach ~44px; padding replaces the old space-y gaps */}
                <div className="min-w-0 text-xs font-light text-stone-300">
                    <p className="flex items-start gap-2 py-1.5 leading-relaxed group-hover:text-white transition-colors">
                        <MapPin size={14} className="text-gold shrink-0 mt-0.5" />
                        <span className="min-w-0 break-words">{office.address}</span>
                    </p>

                    <a href={`mailto:${office.email}`} className="flex min-w-0 items-start gap-2 py-3.5 hover:text-gold transition-colors">
                        <Mail size={14} className="text-gold shrink-0 mt-0.5" />
                        <span className="min-w-0 break-words leading-relaxed">{renderEmail(office.email)}</span>
                    </a>

                    {office.phone && (
                    <a href={`tel:${office.phone}`} className="flex min-w-0 items-center gap-2 py-3.5 hover:text-gold transition-colors">
                        <Phone size={14} className="text-gold shrink-0" />
                        <span className="min-w-0 whitespace-nowrap">{office.phone}</span>
                    </a>
                    )}

                    {office.phones && office.phones.map((phone, idx) => (
                    <a key={idx} href={`tel:${phone}`} className="flex min-w-0 items-center gap-2 py-3.5 hover:text-gold transition-colors">
                        <Phone size={14} className="text-gold shrink-0" />
                        <span className="min-w-0 whitespace-nowrap">{phone}</span>
                    </a>
                    ))}
                </div>
            </div>
        ))}
        </div>

        {/* Sitemap */}
        <div className="border-t border-white/10 pt-12 pb-10 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <picture>
              <source srcSet="/hillpoint-logo.webp" type="image/webp" />
              <img
                src="/hillpoint-logo.png"
                alt="HILLPOINT - Acoustic Solutions"
                loading="lazy"
                width={528}
                height={120}
                className="h-12 w-auto object-contain mb-4"
              />
            </picture>
            <p className="text-stone-300 text-sm font-light leading-relaxed max-w-sm mb-6">
              Manufacturing and installing acoustic panels, soundproofing systems, and architectural acoustics across Qatar, UAE, Saudi Arabia, and India.
            </p>
            <a
              href="https://www.linkedin.com/company/hillpoint"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="HillPoint on LinkedIn"
              className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/20 text-stone-300 hover:text-gold hover:border-gold transition-colors duration-300"
            >
              <Linkedin size={18} />
            </a>
          </div>

          <nav aria-label="Company">
            <p className="text-gold text-xs uppercase tracking-widest mb-4 font-bold">Company</p>
            <ul className="space-y-3 text-sm text-stone-300">
              <li><Link to="/about" className="hover:text-gold transition-colors">About Us</Link></li>
              <li><Link to="/approvals" className="hover:text-gold transition-colors">Approvals</Link></li>
              <li><Link to="/careers" className="hover:text-gold transition-colors">Careers</Link></li>
              <li><Link to="/contact" className="hover:text-gold transition-colors">Contact</Link></li>
            </ul>
          </nav>

          <nav aria-label="Solutions and resources">
            <p className="text-gold text-xs uppercase tracking-widest mb-4 font-bold">Solutions</p>
            <ul className="space-y-3 text-sm text-stone-300">
              <li><Link to="/products" className="hover:text-gold transition-colors">Products</Link></li>
              <li><Link to="/projects" className="hover:text-gold transition-colors">Projects</Link></li>
              <li><Link to="/blog" className="hover:text-gold transition-colors">Acoustic Insights</Link></li>
              <li><Link to="/media" className="hover:text-gold transition-colors">Media</Link></li>
            </ul>
          </nav>
        </div>

        {/* Footer Bottom */}
        <div className="border-t border-white/10 pt-8 flex justify-center items-center">
            <p className="text-stone-400 text-sm font-light text-center">
                © {currentYear} HillPoint Global. All rights reserved.
            </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
